import { getAccessToken, getScopeHeaders } from '../lib/auth-storage';

const API_BASE = import.meta.env.VITE_FINANCECOMMERCE_API ?? '/api/v1';

export type Settlement = {
  id: string;
  settlement_number: string;
  marketplace: string;
  status: string;
  gross_amount: number;
  net_amount: number;
  fee_total: number;
  currency: string;
  health_score: number;
};

export type FinanceDashboard = {
  total_settlements: number;
  pending_recon: number;
  posted_journals: number;
  total_revenue: number;
  total_fees: number;
  net_settlement: number;
  financial_health: number;
  open_exceptions: number;
};

export type JournalVoucher = {
  id: string;
  voucher_number: string;
  status: string;
  total_debit: number;
  total_credit: number;
};

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const token = getAccessToken();
  const res = await fetch(`${API_BASE}${path}`, {
    ...init,
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...getScopeHeaders(),
      ...init?.headers,
    },
  });
  if (!res.ok) throw new Error(await res.text() || res.statusText);
  if (res.status === 204) return undefined as T;
  const json = await res.json();
  return (json.data ?? json) as T;
}

export const financecommerceApi = {
  dashboard: () => request<FinanceDashboard>('/finance/dashboard'),

  listSettlements: () => request<Settlement[]>('/settlements'),

  importSettlement: (body: {
    marketplace: string;
    period_start: string;
    period_end: string;
    transactions: { order_id: string; type: string; amount: number }[];
    fees: { fee_code: string; amount: number }[];
  }) => request<Settlement>('/settlements/import', { method: 'POST', body: JSON.stringify(body) }),

  processSettlement: (settlementId: string) =>
    request<Settlement>('/settlements/process', { method: 'POST', body: JSON.stringify({ settlement_id: settlementId }) }),

  reconcileSettlement: (settlementId: string) =>
    request<unknown>('/settlements/reconcile', { method: 'POST', body: JSON.stringify({ settlement_id: settlementId }) }),

  listJournals: () => request<JournalVoucher[]>('/journals'),

  postJournal: (voucherId: string) =>
    request<JournalVoucher>('/journals/post', { method: 'POST', body: JSON.stringify({ voucher_id: voucherId }) }),

  gstr1: (period = '2026-01') => request<unknown>(`/gst/gstr1?period=${period}`),

  gstr3b: (period = '2026-01') => request<unknown>(`/gst/gstr3b?period=${period}`),

  profitability: () => request<{ revenue: number; net_profit: number; margin_pct: number }>('/finance/profitability'),

  tallyExport: (voucherIds: string[]) =>
    request<unknown[]>('/tally/export', { method: 'POST', body: JSON.stringify({ voucher_ids: voucherIds }) }),
};
